import type { ClientMessage, ServerMessage } from '../protocol/messages';
import { isServerMessage } from '../protocol/guards';
import type { MarketDataTransport, TransportStatus } from './MarketDataTransport';

type MessageListener = (message: ServerMessage) => void;
type StatusListener = (status: TransportStatus, message: string) => void;

export class WorkerMarketDataTransport implements MarketDataTransport {
  private worker?: Worker;
  private messageListeners = new Set<MessageListener>();
  private statusListeners = new Set<StatusListener>();
  private queuedMessages: ClientMessage[] = [];
  private currentStatus: TransportStatus = 'idle';
  private readonly createWorker: () => Worker;

  constructor(createWorker: () => Worker) {
    this.createWorker = createWorker;
  }

  get status(): TransportStatus {
    return this.currentStatus;
  }

  open(): void {
    if (this.worker || this.currentStatus === 'open' || this.currentStatus === 'connecting') return;
    this.setStatus('connecting', 'Starting worker transport');
    const worker = this.createWorker();
    this.worker = worker;

    worker.onmessage = (event: MessageEvent<unknown>) => {
      if (!isServerMessage(event.data)) {
        this.setStatus('error', 'Dropped invalid server message');
        return;
      }
      const message = event.data;
      this.messageListeners.forEach((listener) => listener(message));
    };

    worker.onerror = () => {
      this.setStatus('error', 'Worker transport error');
    };

    worker.onmessageerror = () => {
      this.setStatus('error', 'Worker message serialization error');
    };

    this.setStatus('open', 'Worker transport open');
    const messages = this.queuedMessages;
    this.queuedMessages = [];
    messages.forEach((message) => this.send(message));
  }

  close(): void {
    this.worker?.terminate();
    this.worker = undefined;
    this.queuedMessages = [];
    this.setStatus('closed', 'Worker transport closed');
  }

  send(message: ClientMessage): void {
    if (!this.worker) {
      this.queuedMessages.push(message);
      return;
    }
    this.worker.postMessage(message);
  }

  onMessage(listener: MessageListener): () => void {
    this.messageListeners.add(listener);
    return () => this.messageListeners.delete(listener);
  }

  onStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  private setStatus(status: TransportStatus, message: string): void {
    this.currentStatus = status;
    this.statusListeners.forEach((listener) => listener(status, message));
  }
}
